import { SoFar } from '../SoFar';
import { useApp } from '../context';
import { useAsync } from '../hooks';
import { Bar, Dismiss, Empty, ErrorNote, Loading, Modal, Panel, Stat, StateBadge, TrendChart } from '../ui';
import { LedgerStrip, useWide } from '../ledger';
import { latestPublished, monthKey, previousPublished, publishedMonths } from '../../lib/periods';
import { DASH, dateLabel, deltaLabel, money, monthLabel, naira, nairaShort, shortMonthLabel } from '../../lib/format';
import { navigate, replaceRoute } from '../../lib/route';
import type { CategoryMonth, Month } from '../../data/types';

const ALL = 'All transactions';
const SPENDING_TYPES = new Set(['lifestyle', 'fixed', 'exceptional', 'other']);

const arrow = (d: string) => (d === 'up' ? '↑' : d === 'down' ? '↓' : '');

export function periodState(m: Month, openStart: string | undefined) {
  if (m.status === 'published') return 'Published Actuals' as const;
  if (m.month_start === openStart) return 'Open Plan' as const;
  return 'Review' as const;
}

export function Spend({ month: routeMonth, category }: { month?: string; category?: string }) {
  const { repo, base } = useApp();
  const wide = useWide();
  const published = publishedMonths(base.months);
  const fallback = latestPublished(base.months) ?? base.openPlan;
  const selected = (routeMonth && base.months.find((m) => monthKey(m.month_start) === routeMonth)) || fallback;

  const prior = selected ? previousPublished(base.months, selected.month_start) : null;
  const rows = useAsync(() => (selected ? repo.categoryMonths(selected.id) : Promise.resolve([])), [selected?.id]);
  const priorRows = useAsync(() => (prior ? repo.categoryMonths(prior.id) : Promise.resolve([])), [prior?.id]);

  if (!selected) return <Panel><Empty>No months exist yet.</Empty></Panel>;
  const state = periodState(selected, base.openPlan?.month_start);
  const isActual = state !== 'Open Plan';
  const key = monthKey(selected.month_start);

  const priorBy = new Map((priorRows.data ?? []).map((r) => [r.category_id, r]));

  const spending = (rows.data ?? []).filter((r) => SPENDING_TYPES.has(r.category_type));
  const capital = (rows.data ?? []).filter((r) => r.category_type === 'investment' || r.category_type === 'property' || r.category_type === 'business');
  spending.sort((a, b) => (isActual ? Number(b.actual_amount ?? 0) - Number(a.actual_amount ?? 0) : Number(b.budget_amount ?? 0) - Number(a.budget_amount ?? 0)));

  const summary = (label: string, actual: number | null, cap: number | null, capWord: string, priorValue: number | null | undefined) => {
    const d = isActual && prior ? deltaLabel(actual, priorValue) : null;
    return (
      <Stat
        label={label}
        value={isActual ? nairaShort(actual) : DASH}
        foot={<>{capWord} {nairaShort(cap)}{d && <> · <span className="delta">{arrow(d.direction)} {d.text} than {new Date(`${prior!.month_start}T00:00:00`).toLocaleDateString('en-GB', { month: 'short' })}</span></>}</>}
      />
    );
  };

  const row = (r: CategoryMonth) => {
    const actual = Number(r.actual_amount ?? 0);
    const budget = r.budget_amount;
    const p = priorBy.get(r.category_id);
    const d = isActual && p ? deltaLabel(actual, p.actual_amount) : null;
    return (
      <li key={r.category_id}>
        <button className={`cat-row ${category === r.name ? 'selected' : ''}`} onClick={() => navigate({ section: 'spend', month: key, category: r.name })}>
          <span className="cat-name">{r.name}</span>
          <span className="cat-bar">{budget ? <Bar value={actual} max={Number(budget)} over={isActual && actual > Number(budget)} /> : null}</span>
          <span className="cat-amount">{isActual ? naira(actual) : DASH}<small>{budget ? `of ${nairaShort(budget)}` : 'no cap'}</small></span>
          <span className="cat-delta">{d ? `${arrow(d.direction)} ${d.text}` : ''}</span>
        </button>
      </li>
    );
  };

  const evidenceCategory = category === ALL ? null : (rows.data ?? []).find((r) => r.name === category) ?? null;

  return (
    <div className="stack">
      <Panel className="period-bar">
        <div className="period-row">
          <div>
            <h2>{monthLabel(selected.month_start)} <StateBadge state={state} /></h2>
          </div>
        </div>
        <LedgerStrip months={base.months} openStart={base.openPlan?.month_start} selected={key}
          onSelect={(k) => navigate({ section: 'spend', month: k })} label="Choose a month" />
        {!isActual && <p className="muted">Budgets below are the plan. Confirmed spending appears once the month is closed; until then the figures come from bank alerts.</p>}
        {state === 'Review' && <p className="muted">Statements are processed but the month is not published. These figures can still change.</p>}
      </Panel>

      {!isActual && base.openPlan?.id === selected.id && <SoFar plan={selected} />}

      {isActual && <div className="stats-3 summary">
        {summary('Personal spend', selected.personal_spend_actual, selected.personal_spend_cap, 'Ceiling', prior?.personal_spend_actual)}
        {summary('Lifestyle', selected.lifestyle_spend_actual, selected.lifestyle_spend_cap, 'Ceiling', prior?.lifestyle_spend_actual)}
        {summary('Invested', selected.investment_actual, selected.investment_target, 'Target', prior?.investment_actual)}
      </div>}

      {published.length > 1 && (
        <Panel title="Personal spend, last 6 months" meta="Against each month's ceiling">
          <TrendChart
            title="Personal spend by month against its ceiling"
            points={published.slice(0, 6).reverse().map((m) => ({
              key: monthKey(m.month_start),
              label: new Date(`${m.month_start}T00:00:00`).toLocaleDateString('en-GB', { month: 'short' }),
              value: m.personal_spend_actual, cap: m.personal_spend_cap, selected: m.id === selected.id,
            }))}
            onSelect={(k) => navigate({ section: 'spend', month: k })}
          />
        </Panel>
      )}

      {wide ? (
        <div className="panes">
          <div className="stack">
      <Panel title="Categories" meta={isActual ? (prior ? `vs ${shortMonthLabel(prior.month_start)}` : 'No earlier published month') : 'Budgets'}>
        {rows.error ? <ErrorNote message={rows.error} onRetry={rows.reload} />
          : rows.loading && !rows.data ? <Loading />
          : spending.length ? <ul className="cat-list">{spending.map(row)}</ul>
          : <Empty>No category figures for this month.</Empty>}
        {isActual && <button className="link" onClick={() => navigate({ section: 'spend', month: key, category: ALL })}>All transactions →</button>}
      </Panel>

      {capital.length > 0 && (
        <Panel title="Outside lifestyle" meta="Investment, property and business capital">
          <ul className="cat-list">{capital.map(row)}</ul>
        </Panel>
      )}

          </div>
          <div className="pane-side">
            {category && (category === ALL || evidenceCategory)
              ? <Evidence asPanel monthStart={selected.month_start} categoryId={evidenceCategory?.category_id} title={evidenceCategory?.name ?? ALL}
                  onClose={() => replaceRoute({ section: 'spend', month: key })} />
              : <Panel title="Evidence"><Empty>Choose a category to see its transactions here.</Empty></Panel>}
          </div>
        </div>
      ) : (
        <>
      <Panel title="Categories" meta={isActual ? (prior ? `vs ${shortMonthLabel(prior.month_start)}` : 'No earlier published month') : 'Budgets'}>
        {rows.error ? <ErrorNote message={rows.error} onRetry={rows.reload} />
          : rows.loading && !rows.data ? <Loading />
          : spending.length ? <ul className="cat-list">{spending.map(row)}</ul>
          : <Empty>No category figures for this month.</Empty>}
        {isActual && <button className="link" onClick={() => navigate({ section: 'spend', month: key, category: ALL })}>All transactions →</button>}
      </Panel>

      {capital.length > 0 && (
        <Panel title="Outside lifestyle" meta="Investment, property and business capital">
          <ul className="cat-list">{capital.map(row)}</ul>
        </Panel>
      )}

      {category && (category === ALL || evidenceCategory) && (
        <Evidence
          monthStart={selected.month_start}
          categoryId={evidenceCategory?.category_id}
          title={evidenceCategory?.name ?? ALL}
          onClose={() => replaceRoute({ section: 'spend', month: key })}
        />
      )}
        </>
      )}
    </div>
  );
}

function Evidence({ monthStart, categoryId, title, onClose, asPanel = false }: { monthStart: string; categoryId?: string; title: string; onClose: () => void; asPanel?: boolean }) {
  const { repo } = useApp();
  const tx = useAsync(() => repo.transactions(monthStart, categoryId), [monthStart, categoryId]);
  const debits = (tx.data ?? []).filter((t) => t.direction === 'debit' && t.currency === 'NGN').reduce((s, t) => s + t.amount, 0);
  const body = (
    <>
      {tx.error ? <ErrorNote message={tx.error} onRetry={tx.reload} />
        : tx.loading ? <Loading />
        : !tx.data?.length ? <Empty>No transactions recorded for this category.</Empty>
        : (
          <>
            <p className="muted">{tx.data.length} transaction{tx.data.length === 1 ? '' : 's'}, {naira(debits)} spent in naira</p>
            <ul className="tx-list">
              {tx.data.map((t) => (
                <li key={t.id}>
                  <div>
                    <strong>{t.description}</strong>
                    <span>{dateLabel(t.tx_date)}{t.account_label ? ` · ${t.account_label}` : ''}{t.needs_review ? ' · needs review' : ''}</span>
                  </div>
                  <b className={t.direction === 'credit' ? 'credit' : ''}>{t.direction === 'credit' ? '+' : '−'}{money(t.amount, t.currency)}</b>
                </li>
              ))}
            </ul>
          </>
        )}
    </>
  );
  if (asPanel) {
    return (
      <Panel title={`${title}, ${monthLabel(monthStart)}`} meta={<button className="link" onClick={onClose}>Close</button>}>
        {body}
      </Panel>
    );
  }
  return (
    <Modal title={`${title}, ${monthLabel(monthStart)}`} onClose={onClose}
      footer={<Dismiss>Done</Dismiss>}>
      {body}
    </Modal>
  );
}

